/// <reference types="@types/google.maps" />
import { Logger } from '../../02_utils/logger';
import { mapStyles } from './mapStyles';
import { Loader } from '@googlemaps/js-api-loader';

/**
 * Handles the Map behaviour;
 * Only loads map, when a map component gets detected on the current page.
 */
export class GoogleMap {
  private maps: NodeListOf<HTMLElement> = document.querySelectorAll('.js-c-map--google');
  private markers: Array<Array<google.maps.Marker>> = new Array();
  private bounds: Array<google.maps.LatLngBounds> = new Array();

  constructor(private $log: Logger) {
    if (this.maps.length) {
      const firstElement = this.maps[0];
      const apiKey = firstElement.dataset.key;

      if (apiKey != '') {
        const loader = new Loader({
          apiKey,
          version: 'weekly',
          libraries: ['places'],
        });

        loader.load().then(() => {
            this.$log.debug('Map: ', this.maps);
            this.maps.forEach((map: HTMLElement, index: number) => {
              const location: JSON | null = map.dataset.location ? JSON.parse(map.dataset.location) : null;
              this.markers[index] = new Array();
              this.bounds[index] = new google.maps.LatLngBounds();
              this.initMap(map, location, index);
            });
          }).catch((e) => {
            this.$log.error('Map: ', e);
          });
      }
    }
  }

  /**
   * Initializes the google map for all the map components on the page.
   * @param mapHTMLElement: The element the map should be rendered in
   * @param location the json with the location elements
   * @param index the current loop index
   */
  private initMap(mapHTMLElement: HTMLElement, location: JSON, index: number) {
    const map = new google.maps.Map(mapHTMLElement, this.mapOptions());

    this.setMarker(map, location, index);
    if (this.markers[index].length) {
      this.setBounds(map, this.markers[index], index);
    }
  }

  /**
   * Place all the markers onto the map
   * @param map the map the marker should be placed
   * @param location the location JSON with the marker information
   * @param index the current loop index
   */
  private setMarker(map: google.maps.Map, location: JSON, index: number) {
    Object.values(location).forEach((value) => {
      const latitude: number = value.latitude;
      const longitude: number = value.longitude;
      const icon: string = value.typ?.icon;
      const title: string = value.information?.title;
      const information: google.maps.InfoWindow = new google.maps.InfoWindow({ content: value.information.content, maxWidth: 300 });
      const marker = new google.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map: map,
        icon: icon,
        title: title,
      });

      this.markers[index].push(marker);

      google.maps.event.addListener(marker, 'click', () => {
        information.open(marker.get('map'), marker);
        map.setCenter(marker.getPosition());
      });
    });
  }

  private mapOptions(): google.maps.MapOptions {
    return {
      zoom: 6,
      center: { lat: 51.1642292, lng: 10.4541194 },
      streetViewControl: false,
      styles: mapStyles,
    };
  }

  /**
   * Set the map bounds to show all marker
   * @param map the map for the boundary
   * @param markers all the markers on this specific map
   * @param index the current loop index
   */
  private setBounds(map: google.maps.Map, markers: Array<google.maps.Marker>, index: number): void {
    this.$log.debug('Map: Marker: ', markers);

    markers.forEach((marker) => {
      const latLng = new google.maps.LatLng(marker.getPosition());
      this.bounds[index].extend(latLng);
    });

    this.$log.debug('Map: Bounds', this.bounds);

    map.fitBounds(this.bounds[index]);
    map.panToBounds(this.bounds[index]);
    map.setOptions({ maxZoom: 16 });
    map.setOptions({ minZoom: 2 });
  }
}
